<template>
  <div class="container">
    <h1>Reservaciones Restaurante Merced</h1>
    <form @submit.prevent="makeReservation">
      <div>
        <label for="name">Nombre:</label>
        <input type="text" v-model="state.reservation.name" id="name" required>
      </div>
      <div>
        <label for="email">Email:</label>
        <input type="email" v-model="state.reservation.email" id="email" required>
      </div>
      <div>
        <label for="phone">Teléfono:</label>
        <input type="tel" v-model="state.reservation.phone" id="phone" required>
      </div>
      <div>
        <label for="date">Fecha:</label>
        <input type="date" v-model="state.reservation.date" id="date" required @change="checkRules">
      </div>
      <div>
        <label for="time">Hora:</label>
        <select v-model="state.reservation.time" id="time" required>
          <option v-for="time in availableTimes" :key="time" :value="time">{{ formatTime(time) }}</option>
        </select>
        <p v-if="timeError">{{ timeError }}</p>
      </div>
      <div>
        <label for="people">Número de personas:</label>
        <input type="number" v-model="state.reservation.people" id="people" required min="1" :max="maxPeople">
      </div>
      <div>
        <label for="specialNotes">Notas Especiales:</label>
        <textarea v-model="state.reservation.specialNotes" id="specialNotes" :disabled="noSpecialNotes" placeholder="Escriba sus notas aquí..."></textarea>
      </div>
      <div>
        <input type="checkbox" v-model="noSpecialNotes" id="noSpecialNotes">
        <label for="noSpecialNotes">No tengo notas especiales</label>
      </div>
      <p v-if="specialNotesError">{{ specialNotesError }}</p>
      <button type="submit" :disabled="v$.reservation.$invalid || timeError || loading">
        <template v-if="loading">
          <span class="spinner"></span> Procesando...
        </template>
        <template v-else>
          Reservar
        </template>
      </button>
    </form>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email, minLength } from '@vuelidate/validators';
import { collection, addDoc, getDocs, query, where, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/init';
import { sendReservationReceivedNotification } from '../utils/reservation';

export default {
  name: 'ClientView',
  setup() {
    const state = ref({
      reservation: {
        name: '',
        email: '',
        phone: '',
        date: null, // Fecha inicial como null
        time: '',
        people: '1',
        specialNotes: '', // Notas iniciales vacías
      },
    });

    const maxPeople = ref(50);
    const timeError = ref(''); // Sin mensaje inicial
    const specialNotesError = ref('');
    const noSpecialNotes = ref(false);
    const loading = ref(false);
    const availableTimes = ref([]);
    const disabledTimes = ref({});
    const shouldValidate = ref(true); // Permitir validación por defecto

    const OPENING_HOUR = 11;
    const CLOSING_HOUR = 21;
    const SLOT_INTERVAL_MINUTES = 30;
    const MAX_PEOPLE_PER_SLOT = 100;

    const generateTimeSlots = () => {
      const slots = [];
      let minutes = OPENING_HOUR * 60;
      const closingMinutes = CLOSING_HOUR * 60;

      while (minutes <= closingMinutes) {
        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;
        const hourStr = String(hour).padStart(2, '0');
        const minuteStr = String(minute).padStart(2, '0');
        slots.push(`${hourStr}:${minuteStr}`);
        minutes += SLOT_INTERVAL_MINUTES;
      }

      return slots;
    };

    const baseTimes = generateTimeSlots();



    const rules = {
      reservation: {
        name: { required },
        email: { required, email },
        phone: { required, minLength: minLength(10) },
        date: { required },
        time: { required },
        people: { required },
        specialNotes: {
          required: () =>
            !noSpecialNotes.value ||
            state.value.reservation.specialNotes.trim() !== '',
        },
      },
    };

    const v$ = useVuelidate(rules, state);

    // Detecta cambios en la fecha seleccionada
    watch(
      () => state.value.reservation.date,
      () => {
        if (state.value.reservation.date) {
          checkRules(); // Ejecutar solo si hay una fecha seleccionada
        }
      }
     );

     onMounted(() => {
    const adminConfigRef = doc(db, 'adminConfig', 'reservations'); // Cambia el nombre del documento si es diferente
    onSnapshot(adminConfigRef, (doc) => {
      if (doc.exists()) {
        disabledTimes.value = doc.data().disabledTimes || {}; // Cargar horarios desactivados
        console.log('Horarios desactivados:', disabledTimes.value);
      } else {
        console.log('No se encontraron horarios desactivados.');
      }
    });
  });


  

const checkRules = () => {
  if (!shouldValidate.value) {
    console.log('Validación desactivada temporalmente.');
    return; // No validar si la validación está desactivada
  }

  timeError.value = ''; // Reiniciar mensaje de error
  availableTimes.value = []; // Reiniciar las horas disponibles

  if (!state.value.reservation.date) {
    // Mostrar mensaje solo si no hay fecha seleccionada
    if (state.value.reservation.date === null || state.value.reservation.date === '') {
      timeError.value = 'Por favor, selecciona una fecha.';
    }
    return;
  }

  const selectedDate = new Date(state.value.reservation.date);
  const dayIndex = selectedDate.getDay(); // 0 = domingo, 1 = lunes, ..., 6 = sábado

// ✅ Verificar si el día está entre miércoles (3) y domingo (0)
if (![3, 4, 5, 6, 0].includes(dayIndex)) {
  timeError.value = 'Las reservaciones solo están disponibles de miércoles a domingo.';
  availableTimes.value = [];
  return;
}

// ✅ Horarios permitidos para todos los días válidos (de 11:00 AM a 9:00 PM)

  // Filtrar horarios desactivados
  const selectedDateISO = selectedDate.toISOString().split('T')[0];
  const disabled = disabledTimes.value[selectedDateISO] || [];
  console.log('Horarios base:', baseTimes);
  console.log('Horarios desactivados para', selectedDateISO, ':', disabled);

  availableTimes.value = baseTimes.filter((time) => !disabled.includes(time)); // Excluir horarios desactivados

  if (availableTimes.value.length === 0) {
    timeError.value = `No hay horarios disponibles para la fecha seleccionada (${selectedDateISO}).`;
  } else {
    console.log('Horarios disponibles:', availableTimes.value);
  }
  
  filterAvailableTimes(); // Filtrar más si es necesario
};


const filterAvailableTimes = async () => {
  const reservedTimes = await getReservedTimes(state.value.reservation.date);

  const filtered = availableTimes.value.filter((time) => {
    const totalPeopleForTime = reservedTimes[time] || 0;
    return totalPeopleForTime < MAX_PEOPLE_PER_SLOT;
  });

  if (filtered.length === 0) {
    timeError.value = 'Todos los horarios para este día están llenos. Por favor elige otra hora o fecha.';
  } else {
    timeError.value = '';
  }

  availableTimes.value = filtered;
};

const getTimeBlocks = (startTime) => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const blocks = [];

  let currentHour = hours;
  let currentMinutes = minutes;

  for (let i = 0; i < 4; i++) {
    const h = String(currentHour).padStart(2, '0');
    const m = String(currentMinutes).padStart(2, '0');
    blocks.push(`${h}:${m}`);

    currentMinutes += 30;
    if (currentMinutes >= 60) {
      currentMinutes = 0;
      currentHour += 1;
    }
  }

  return blocks;
};

const getReservedTimes = async (dateStr) => {
  const q = query(
    collection(db, 'reservations'),
    where('date', '==', dateStr)
  );

  const snapshot = await getDocs(q);

  const reservedTimes = {}; // clave: hora, valor: total personas

  snapshot.forEach((doc) => {
    const data = doc.data();
    const startTime = data.time; // por ej. '16:00'
    const people = parseInt(data.people, 10) || 1;

    const timeBlocks = getTimeBlocks(startTime); // obtiene los 4 bloques

    timeBlocks.forEach(time => {
      reservedTimes[time] = (reservedTimes[time] || 0) + people;
    });
  });

  return reservedTimes;
};

    
   const makeReservation = async () => {
  v$.value.$touch();
  if (v$.value.$invalid || timeError.value) {
    console.error('Validation failed');
    specialNotesError.value = 'Debe escribir notas especiales o seleccionar la opción "No tengo notas especiales".';
    return;
  }
  loading.value = true;
  try {
    // Obtener el número de personas reservadas en el horario seleccionado
    const reservedTimes = await getReservedTimes(state.value.reservation.date);
    const totalPeopleForTime = reservedTimes[state.value.reservation.time] || 0;

    // Si el total de personas supera el límite de 100, mostrar un mensaje y no permitir la reservación
    if (totalPeopleForTime + parseInt(state.value.reservation.people) > MAX_PEOPLE_PER_SLOT) {
      alert('El límite de 100 personas por hora ha sido alcanzado. Por favor, comuníquese con el restaurante para más detalles.');
      loading.value = false;
      return;
    }

    // Si no se ha alcanzado el límite, proceder a crear la reservación
    const docRef = await addDoc(collection(db, 'reservations'), { ...state.value.reservation, status: 'pending' });
    console.log('Reservation added with ID: ', docRef.id);
    state.value.reservation.id = docRef.id;
    await sendReservationReceivedNotification(state.value.reservation);
    alert(`Solicitud pendiente, Verifique email para detalles,ID: ${docRef.id}`);
    state.value.reservation = {
      name: '',
      email: '',
      phone: '',
      date: null, // Reiniciar como null
      time: '',
      people: '1',
      specialNotes: '' // Restablecer notas especiales
    };
    noSpecialNotes.value = false;
    v$.value.$reset();
  } catch (error) {
    console.error('Error al crear la reservación:', error);
  } finally {
    loading.value = false;
  }
};



    const formatTime = (time) => {
  const [hour, minute] = time.split(':');
  const date = new Date();
  date.setHours(hour, minute);
  const options = { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Puerto_Rico' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

    watch(() => state.value.reservation.date, checkRules);

    return {
      v$,
      state,
      disabledTimes,
      maxPeople,
      makeReservation,
      checkRules,
      timeError,
      specialNotesError,
      noSpecialNotes,
      loading,
      availableTimes,
      formatTime,
      getTimeBlocks,
    };
  }
};
</script>

<style scoped>
.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
}

label {
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
  color: #333;
}

input,
select,
textarea {
  width: 100%;
  padding: 12px;
  margin-bottom: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-sizing: border-box;
  font-size: 16px;
}

textarea {
  resize: vertical;
  min-height: 100px;
}

button {
  width: 100%;
  padding: 14px;
  background-color: #007bff;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  font-size: 16px;
  transition: background-color 0.2s ease-in-out;
}

button:hover {
  background-color: #0056b3;
}

button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

p {
  color: #d9534f;
  margin-top: 10px;
  font-size: 14px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #ffffff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 📱 Responsive */
@media (max-width: 600px) {
  .container {
    padding: 16px;
  }

  input,
  select,
  textarea,
  button {
    font-size: 14px;
    padding: 10px;
  }

  button {
    font-size: 15px;
  }
}
</style>
